import React, { useState } from 'react';
import { cn } from '@shared/lib/utils';
import type { SidebarItem } from '../model/sidebar.types';
import { SidebarMenuButton } from './sidebar-menu-button';
import { ChevronDown } from 'lucide-react';
import { getIcon } from '../lib/icon-map';

interface SidebarMenuItemProps {
  item: SidebarItem;
  onItemClick?: (item: SidebarItem) => void;
  collapsed?: boolean;
  depth?: number;
}

export function SidebarMenuItem({
  item,
  onItemClick,
  collapsed,
  depth = 0,
}: SidebarMenuItemProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(item.defaultOpen ?? false);
  const hasChildren = item.children && item.children.length > 0;
  const icon = getIcon(item.icon);

  const handleClick = () => {
    if (hasChildren && item.collapsible) {
      setIsOpen(!isOpen);
    }
    onItemClick?.(item);
  };

  return (
    <div className="flex flex-col w-full">
      <SidebarMenuButton
        icon={icon}
        label={item.label}
        isActive={item.active}
        collapsed={collapsed}
        onClick={handleClick}
        className={cn(depth > 0 && 'ml-4')}
        badge={item.badge}
      >
        {hasChildren && !collapsed && (
          <ChevronDown
            className={cn(
              'ml-auto size-5 shrink-0 text-sidebar-foreground transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            strokeWidth={1.5}
          />
        )}
      </SidebarMenuButton>

      {hasChildren && isOpen && !collapsed && (
        <div className="flex flex-col gap-1 mt-1 w-full">
          {item.children!.map((child) => (
            <SidebarMenuItem
              key={child.id}
              item={child}
              onItemClick={onItemClick}
              collapsed={collapsed}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
